/**
 * Percent fossil primary heating (ACS B25040-style fuels) and California peer burden percentile
 * for the Climate Equity Index. Source: Supabase `house_heating_fuel` (same as HouseHeatingFuelSupabase).
 *
 * Fossil = utility gas + bottled/LP + fuel oil + coal/coke (% of households).
 * Burden: higher fossil share → higher percentile (worse), consistent with other burden metrics.
 */

import { supabaseClient } from './supabase';

const TARGET_YEAR = 2023;

const FOSSIL_FUELS_LOWER = new Set([
  'utility gas',
  'natural gas',
  'bottled, tank, or lp gas',
  'fuel oil, kerosene, etc.',
  'coal or coke',
]);

type HeatingRow = {
  geofips?: string | number | null;
  geography_type?: string | null;
  house_heating_fuel?: string | null;
  year?: number | null;
  percent_hh?: number | string | null;
  percent_of_households?: number | string | null;
};

function toNum(v: number | string | null | undefined): number | null {
  if (v === null || v === undefined) return null;
  const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
}

function normalizeGeoType(g: string | null | undefined): string {
  return String(g ?? '').trim();
}

/** Single canonical form for grouping (matches common Supabase text geofips). */
function canonicalGeofips(g: string | number | null | undefined): string | null {
  if (g === null || g === undefined) return null;
  const s = String(g).trim();
  if (!s) return null;
  if (!/^\d+$/.test(s)) return s;
  if (s.length === 6) return '0' + s;
  if (s.length === 4) return '0' + s;
  if (s.length === 1) return '0' + s;
  return s;
}

function geofipsLookupVariants(geofips: number): string[] {
  const s = String(geofips).trim();
  const out = new Set<string>([s]);
  const c = canonicalGeofips(s);
  if (c) out.add(c);
  if (/^\d+$/.test(s)) {
    const u = s.replace(/^0+/, '') || '0';
    out.add(u);
  }
  return Array.from(out);
}

function placeKey(geofipsCanon: string, geographyType: string): string {
  return `${geofipsCanon}|${geographyType}`;
}

function rowPct(r: HeatingRow): number | null {
  return toNum(r.percent_hh ?? r.percent_of_households);
}

function fossilPercentForPlaceRows(rows: HeatingRow[]): number | null {
  let sum = 0;
  let any = false;
  for (const r of rows) {
    const fuel = String(r.house_heating_fuel ?? '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
    if (!FOSSIL_FUELS_LOWER.has(fuel)) continue;
    const p = rowPct(r);
    if (p == null) continue;
    sum += p;
    any = true;
  }
  if (!any) return null;
  return Math.min(100, Math.max(0, sum));
}

async function paginateHouseHeatingYear(year: number, selectCols: string): Promise<HeatingRow[]> {
  const pageSize = 1000;
  const all: HeatingRow[] = [];
  for (let from = 0; ; from += pageSize) {
    const { data, error } = await supabaseClient
      .from('house_heating_fuel')
      .select(selectCols)
      .eq('year', year)
      .range(from, from + pageSize - 1);
    if (error) throw error;
    const chunk = (data || []) as HeatingRow[];
    all.push(...chunk);
    if (chunk.length < pageSize) break;
  }
  return all;
}

/** Higher fossil → higher burden percentile (0–100). */
function peerBurdenPercentile(fossil: number, allFossils: number[]): number {
  const n = allFossils.length;
  if (n < 2) return 50;
  const less = allFossils.filter((x) => x < fossil).length;
  const equal = allFossils.filter((x) => x === fossil).length;
  const midRank = less + (equal - 1) / 2;
  return Math.min(100, Math.max(0, Math.round((midRank / (n - 1)) * 100)));
}

type DistCache = {
  year: number;
  burdenPercentileByKey: Map<string, number>;
};

let distCache: DistCache | null = null;

async function buildDistribution(year: number): Promise<DistCache | null> {
  const selectCols = 'geofips, geography_type, house_heating_fuel, year, percent_hh';
  let rows: HeatingRow[];
  try {
    rows = await paginateHouseHeatingYear(year, selectCols);
  } catch (e) {
    return null;
  }

  if (!rows.length) return null;

  const byPlace = new Map<string, HeatingRow[]>();
  for (const r of rows) {
    const canon = canonicalGeofips(r.geofips);
    const gt = normalizeGeoType(r.geography_type);
    if (!canon || !gt) continue;
    const key = placeKey(canon, gt);
    if (!byPlace.has(key)) byPlace.set(key, []);
    byPlace.get(key)!.push(r);
  }

  const fossilByKey = new Map<string, number>();
  for (const [key, prs] of Array.from(byPlace.entries())) {
    const fp = fossilPercentForPlaceRows(prs);
    if (fp != null) fossilByKey.set(key, fp);
  }

  const allFossils = Array.from(fossilByKey.values());
  const burdenPercentileByKey = new Map<string, number>();
  for (const [key, fossil] of Array.from(fossilByKey.entries())) {
    burdenPercentileByKey.set(key, peerBurdenPercentile(fossil, allFossils));
  }

  return { year, burdenPercentileByKey };
}

async function getDistribution(year: number): Promise<DistCache | null> {
  if (distCache?.year === year) return distCache;
  const built = await buildDistribution(year);
  if (built) distCache = built;
  return built;
}

function lookupPercentile(
  geofips: number,
  geographyType: string,
  burdenPercentileByKey: Map<string, number>
): number | null {
  const gt = normalizeGeoType(geographyType);
  for (const v of geofipsLookupVariants(geofips)) {
    const canon = canonicalGeofips(v);
    if (!canon) continue;
    const key = placeKey(canon, gt);
    if (burdenPercentileByKey.has(key)) return burdenPercentileByKey.get(key)!;
  }
  return null;
}

/** Try primary geography_type then common variants (house_heating_fuel often uses City vs Place). */
/** When `population` name does not match livability/heating tables, infer geofips from heating rows (same source as Buildings chart). */
async function resolveGeoFromHouseHeatingFuel(locationName: string): Promise<{ geofips: number; geography_type: string } | null> {
  const name = locationName.trim();
  if (!name) return null;
  const parseRow = (row: { geofips?: unknown; geography_type?: unknown } | undefined) => {
    if (!row?.geofips) return null;
    const gf = typeof row.geofips === 'number' ? row.geofips : Number(row.geofips);
    if (!Number.isFinite(gf)) return null;
    const gtype = String(row.geography_type || 'City').trim() || 'City';
    return { geofips: gf, geography_type: gtype };
  };

  const { data: exact, error: e1 } = await supabaseClient
    .from('house_heating_fuel')
    .select('geofips, geography_type')
    .eq('location_name', name)
    .not('geofips', 'is', null)
    .limit(5);
  if (!e1 && exact?.length) {
    const p = parseRow(exact[0] as { geofips?: unknown; geography_type?: unknown });
    if (p) return p;
  }

  const { data: prefix, error: e2 } = await supabaseClient
    .from('house_heating_fuel')
    .select('geofips, geography_type')
    .ilike('location_name', `${name}%`)
    .not('geofips', 'is', null)
    .limit(15);
  if (e2 || !prefix?.length) return null;
  return parseRow(prefix[0] as { geofips?: unknown; geography_type?: unknown });
}

function lookupPercentileWithGeoVariants(
  geofips: number,
  geographyType: string,
  burdenPercentileByKey: Map<string, number>
): number | null {
  const order = Array.from(
    new Set(
      [geographyType, 'City', 'Place', 'County']
        .map((x) => normalizeGeoType(x))
        .filter(Boolean)
    )
  );
  for (const gt of order) {
    const p = lookupPercentile(geofips, gt, burdenPercentileByKey);
    if (p != null) return p;
  }
  return null;
}

/**
 * Burden percentile (0–100, higher = more fossil heating vs peers) for the selected place.
 */
export async function fetchFossilHeatingBurdenPercentile(
  locationName: string,
  geofips: number | null,
  geographyType: string | null
): Promise<number | null> {
  if (!locationName) return null;

  let gf = geofips;
  let gt = geographyType;

  if (gf == null || !gt) {
    const { supabaseLocationService } = await import('./supabaseLocationService');
    let row = await supabaseLocationService.getGeofipsWithGeographyType(locationName);
    if (!row) row = await resolveGeoFromHouseHeatingFuel(locationName);
    if (!row) return null;
    gf = row.geofips;
    gt = row.geography_type;
  }

  if (gf == null || !gt) return null;

  let year = TARGET_YEAR;
  let dist = await getDistribution(year);
  if (dist) {
    const p = lookupPercentileWithGeoVariants(gf, gt!, dist.burdenPercentileByKey);
    if (p != null) return p;
  }

  const variants = geofipsLookupVariants(gf);
  let latestY: number | null = null;
  for (const v of variants) {
    const { data } = await supabaseClient
      .from('house_heating_fuel')
      .select('year')
      .eq('geofips', v)
      .order('year', { ascending: false })
      .limit(1);
    const y = data?.[0]?.year;
    if (typeof y === 'number' && (latestY == null || y > latestY)) latestY = y;
  }
  if (latestY != null && latestY !== year) {
    distCache = null;
    dist = await getDistribution(latestY);
  }

  if (!dist) return null;
  return lookupPercentileWithGeoVariants(gf, gt!, dist.burdenPercentileByKey);
}
